'use client';

import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Script from 'next/script';

import useInterval from './useInterval';

type RecaptchaType = {
    isReady: boolean;

    generateToken: () => Promise<string>;
};

const RecaptchaContext = createContext<RecaptchaType>({
    isReady: false,
    generateToken: async () => '',
});

type Props = PropsWithChildren<{
    action: string;
    keyId: string;
    bottom?: number;
}>;

export function RecaptchaContextProvider(props: Props) {
    const [isReady, setIsReady] = useState(false);

    const setNodeVisibility = useCallback(
        (visible: boolean) => {
            const node = document.querySelector('.grecaptcha-badge') as HTMLElement;
            if (node) {
                node.className = `grecaptcha-badge ${!visible ? 'invisible' : ''}`.trim();
                node.style.bottom = `${props.bottom || 14}px`;
            }
        },
        [props.bottom],
    );

    const interval = useInterval((clear) => {
        if (window.grecaptcha?.enterprise) {
            clear();

            window.grecaptcha.enterprise.ready(() => {
                setNodeVisibility(true);
                setIsReady(true);
            });
        }
    }, 10);

    useEffect(() => {
        return () => {
            setNodeVisibility(false);
        };
    }, [setNodeVisibility]);

    const value = useMemo<RecaptchaType>(() => {
        return {
            isReady,
            generateToken: async () => {
                if (!isReady) {
                    throw 'Not ready';
                }
                return window.grecaptcha.enterprise.execute(props.keyId, { action: props.action });
            },
        };
    }, [isReady, props.action, props.keyId]);

    return (
        <RecaptchaContext.Provider value={value}>
            {props.children}
            <Script src={`//www.google.com/recaptcha/enterprise.js?render=${props.keyId}`} async defer />
        </RecaptchaContext.Provider>
    );
}

// a hook which we are going to use whenever we need data from `RecaptchaProvider`
export default function useRecaptcha() {
    const context = useContext(RecaptchaContext);

    if (!context) {
        throw new Error('You need to wrap RecaptchaProvider.');
    }

    return context;
}
